/** Data-profiling scheduler domain types (UI-level; the run engine is TODO). */

export type Frequency = 'hourly' | 'daily' | 'weekly' | 'monthly' | 'cron';
export type ScanMode = 'full' | 'sample';
export type Priority = 'low' | 'normal' | 'high';
export type Backoff = 'fixed' | 'exponential';
export type RunStatus = 'success' | 'failed' | 'running' | 'never';

export interface ProfileSchedule {
  id: string;
  name: string;
  description: string;
  owner: string;
  enabled: boolean;

  // Target
  datasetIds: string[];
  datasetNames: string[];
  scope: 'all_columns' | 'selected';

  // Schedule
  frequency: Frequency;
  time: string; // HH:MM
  dayOfWeek: number; // 0=Sun … 6=Sat (weekly)
  dayOfMonth: number; // 1–28 (monthly)
  timezone: string;
  cron: string;
  startDate: string;
  endDate: string;

  // Profiling options
  metrics: string[];
  scanMode: ScanMode;
  samplePercent: number;
  incremental: boolean;

  // Execution
  priority: Priority;
  maxConcurrency: number;
  timeoutMinutes: number;
  retries: number;
  backoff: Backoff;

  // Notifications & alerts
  notifyEmails: string;
  notifyOnSuccess: boolean;
  notifyOnFailure: boolean;
  notifyOnAnomaly: boolean;
  webhookUrl: string;
  alertThreshold: number; // alert if composite quality < this % (0 = off)

  // Runtime
  createdAt: string;
  lastRun: string | null;
  lastStatus: RunStatus;
}

export const FREQUENCY_LABELS: Record<Frequency, string> = {
  hourly: 'Hourly',
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  cron: 'Custom (cron)',
};

export const METRIC_OPTIONS: { key: string; label: string }[] = [
  { key: 'row_count', label: 'Row count' },
  { key: 'null_rate', label: 'Null rate (blanks)' },
  { key: 'distinct_count', label: 'Distinct values' },
  { key: 'pattern_conformance', label: 'Pattern conformance' },
  { key: 'freshness_lag', label: 'Freshness' },
];

export const PRIORITY_LABELS: Record<Priority, string> = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
};

export const TIMEZONES = [
  'Asia/Riyadh',
  'Asia/Dubai',
  'Asia/Qatar',
  'Asia/Kuwait',
  'Asia/Bahrain',
  'UTC',
  'Europe/London',
  'America/New_York',
];

export const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function emptySchedule(): ProfileSchedule {
  return {
    id: '',
    name: '',
    description: '',
    owner: 'admin@nlytics.app',
    enabled: true,
    datasetIds: [],
    datasetNames: [],
    scope: 'all_columns',
    frequency: 'daily',
    time: '02:00',
    dayOfWeek: 1,
    dayOfMonth: 1,
    timezone: 'Asia/Riyadh',
    cron: '0 2 * * *',
    startDate: '',
    endDate: '',
    metrics: ['row_count', 'null_rate', 'distinct_count'],
    scanMode: 'full',
    samplePercent: 20,
    incremental: false,
    priority: 'normal',
    maxConcurrency: 2,
    timeoutMinutes: 30,
    retries: 1,
    backoff: 'exponential',
    notifyEmails: '',
    notifyOnSuccess: false,
    notifyOnFailure: true,
    notifyOnAnomaly: true,
    webhookUrl: '',
    alertThreshold: 0,
    createdAt: new Date().toISOString(),
    lastRun: null,
    lastStatus: 'never',
  };
}

/** Human summary of when a schedule runs, e.g. "Daily at 02:00 · Asia/Riyadh". */
export function describeSchedule(s: ProfileSchedule): string {
  const tz = s.timezone;
  switch (s.frequency) {
    case 'hourly':
      return `Every hour · ${tz}`;
    case 'daily':
      return `Daily at ${s.time} · ${tz}`;
    case 'weekly':
      return `Weekly on ${WEEKDAYS[s.dayOfWeek]} at ${s.time} · ${tz}`;
    case 'monthly':
      return `Monthly on day ${s.dayOfMonth} at ${s.time} · ${tz}`;
    case 'cron':
      return `cron: ${s.cron} · ${tz}`;
  }
}

/** Best-effort next-run estimate for display (ignores timezone offset for simplicity). */
export function computeNextRun(s: ProfileSchedule): Date | null {
  if (!s.enabled) return null;
  const now = new Date();
  const [hh, mm] = s.time.split(':').map((v) => parseInt(v, 10));
  const next = new Date(now);
  next.setSeconds(0, 0);

  if (s.frequency === 'hourly') {
    next.setMinutes(0);
    next.setHours(now.getHours() + 1);
    return next;
  }
  next.setHours(hh || 0, mm || 0);
  if (s.frequency === 'daily') {
    if (next <= now) next.setDate(next.getDate() + 1);
    return next;
  }
  if (s.frequency === 'weekly') {
    let delta = (s.dayOfWeek - now.getDay() + 7) % 7;
    if (delta === 0 && next <= now) delta = 7;
    next.setDate(now.getDate() + delta);
    return next;
  }
  if (s.frequency === 'monthly') {
    next.setDate(s.dayOfMonth);
    if (next <= now) next.setMonth(next.getMonth() + 1);
    return next;
  }
  return null; // cron — not computed in the UI stub
}

export function formatRelative(d: Date | null): string {
  if (!d) return '—';
  const diff = d.getTime() - Date.now();
  if (diff <= 0) return 'due now';
  const mins = Math.round(diff / 60000);
  if (mins < 60) return `in ${mins}m`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `in ${hrs}h`;
  return `in ${Math.round(hrs / 24)}d`;
}
